import * as constants from './constants';
import logger from '@eventbrite/client-logger';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { push } from 'react-router-redux';
import { logEvent } from '@eventbrite/statsig';
import { joinPath } from '@eventbrite/path-utils';
import { gettext } from '@eventbrite/i18n';
import { isSubmitting } from 'redux-form';
import {
    initializeApplePayIfExpressCheckout,
    setHasAsyncErrors,
    setIsMakingApplePayPayment,
    setIsApplePaySupported,
} from '../../actions/applePay';
import {
    initializeGooglePayIfExpressCheckout,
    setIsMakingGooglePayPayment,
    updateGooglePayPaymentRequest,
} from '../../actions/googlePay';
import {
    setIsMakingPaypalPayment,
    setPaypalScriptStatusAction,
} from '../../actions/paypal';
import { selectors as appSelectors } from '../../reducers/app';
import {
    getIsAppRebrandActive,
    getIsTicketSelectionCompactSizeFlow,
} from '../../selectors/app';
import { getUrgencySignals } from '../../selectors/app/getUrgencySignals';
import { getIsTicketSelectionButtonDisabledByCaptcha } from '../../selectors/getIsTicketSelectionButtonDisabledByCaptcha';
import {
    BASE_URL,
    CHECKOUT_STEP_CART,
    PROMO_CODE_FORM_NAME,
    TICKETS_PAGE_FORM_NAME,
    TICKETS_FULL_SIZE_PATH,
    TICKET_LEVEL,
    WAITLIST_PATH,
} from '../../constants';
import { isFormSubmitted } from '../../selectors/forms';
import { getTotalCostDisplay } from '../../selectors/order/getTotalCostDisplay';
import {
    isExpressApplePayEnabled,
    getCanMakePaymentsWithActiveCard,
    getIsMakingApplePayPayment,
} from '../../selectors/applePay';
import { getPromoCodeFormProps } from '../../utils/promoCodes';
import { getStatusForEvent } from '../../utils/status';
import {
    getEventTitle,
    getFormattedEventDate,
    getIsDraftEvent,
    getIsReservedSeating,
    getEventGroupType,
    getIsFreeEvent,
    getIsNewlyCreatedGroup,
    getTicketSelectDefaultCtaText,
    getHasTicketLevelWaitlist,
} from '../../selectors/event';
import {
    isApplePayElegible,
    areExpressCheckoutConditionsReady,
} from '../../selectors/expressCheckoutConditions';
import { EventStatus } from '../../constants/eventStatus';
import { getTemplateProps } from '../template';
import { resetEventAndTickets } from '../../actions/initialize';
import { initializeGrylls as initializeGryllsAction } from '../../actions/pixelAnalytics';
import {
    ticketQuantityChange,
    ticketDonationChange,
    ticketVariantChange,
    updateSelectedPaymentConstraints,
    updateSelectedTickets,
} from '../../actions/tickets';
import {
    notifyParentInitialPageFullyLoaded,
    notifyParentTicketQuantityChange,
    notifyParentTicketSelectionLayoutStabilized,
    notifyParentTicketSelectionSubmit,
} from '../../actions/notifyParent';
import {
    getPromoCodeFormActionHandlers,
    promoCodeSubmit as promoCodeSubmitAction,
} from '../../actions/promoCode';
import {
    createAndPlaceOrderExpressCheckout,
    startExpressCheckoutApplePaySession,
    submitFromTicketSelectionPage,
} from '../../actions/order/common';
import { getEventLevelValueForKey } from './utils/ticketUtils';
import {
    areQuantitySelectorsHidden,
    getFormattedTicketClassesByCategory,
    getIsMultiTicketEvent,
    getShouldShowTicketCategoryGroupHeaders,
} from '../../selectors/tickets';
import { getSelectedTickets } from '../../selectors/tickets/getSelectedTickets';
import { hasSelectedTickets } from '../../selectors/tickets/hasSelectedTickets';
import {
    goToPageBeforeJoin as goToPageBeforeJoinAction,
    resetGroupRegistration as resetGroupRegistrationAction,
} from '../../actions/groupRegistration';
import {
    trackBackClick,
    trackCartIconClick,
    trackJoinWaitlistClick,
    trackPromoCodeFormShown,
} from '../../actions/eventAnalytics';
import { STATSIG_EVENT_NAMES } from '../../constants/statsig';
import { getExpressCheckoutConditions } from '../../actions/expressCheckout';
import { getNosRollout } from '../../actions/nosRollout';
import { getSalesDateForAllTickets } from './utils/ticketDateUtils';
import { navigateToSeriesPage as navigateToSeriesPageAction } from '../../actions/series';
import { shouldShowBackToSeriesLink } from '../series/utils';
import { updateWaitingRoomStep as updateWaitingRoomStepAction } from '../../actions/waitingRoom';
import { PaypalScriptStatus } from '../../state/paypal';
import {
    isExpressPaypalEnabled,
    getIsMakingPaypalPayment,
} from '../../selectors/paypal';
import {
    getGooglePayPaymentRequest,
    isExpressGooglePayEnabled,
    getIsMakingGooglePayPayment,
} from '../../selectors/googlePay';
import { isSeriesInfiniteCalendar } from '../../selectors/infiniteCalendarSeries/infiniteCalendarSeries';
import { loadEmailPreferences } from '../../actions/userPreferences';

export const getTicketClassMetaProps = (state) => {
    const ticketClasses = Object.keys(state.ticketsById).map(
        (t) => state.ticketsById[t],
    );
    const statusForAllTickets = getEventLevelValueForKey(
        ticketClasses,
        'statusKey',
    );
    const salesDateForAllTickets = getSalesDateForAllTickets(
        ticketClasses,
        statusForAllTickets,
    );

    // TODO: consider moving logic to a selector
    const shouldHideStatusOnTickets = !!(
        statusForAllTickets &&
        salesDateForAllTickets &&
        !(
            statusForAllTickets === constants.UNAVAILABLE ||
            statusForAllTickets === constants.SOLD_OUT
        )
    );

    return {
        statusForAllTickets,
        salesDateForAllTickets,
        shouldHideStatusOnTickets,
    };
};

const _getEventResellerProps = ({ ticketReseller, isSoldOut }) =>
    !isEmpty(ticketReseller)
        ? { ...ticketReseller, isEventSoldOut: isSoldOut }
        : {};

const getFooterProps = (state) => {
    const ctaText = getTicketSelectDefaultCtaText(state);
    const eventStatus = getStatusForEvent(
        state.event,
        state.tickets,
        state.app.waitlistCode,
    );
    const hasTicketsLevelWaitlist = getHasTicketLevelWaitlist(state);
    const isJoiningGlobalWaitlist =
        !hasTicketsLevelWaitlist &&
        eventStatus === EventStatus.soldOutWithCapacityWaitlist;

    const validationErrorMessage =
        !hasSelectedTickets(state) && !isJoiningGlobalWaitlist
            ? gettext('Add at least 1 ticket')
            : null;

    const isFormSubmittedBool = isFormSubmitted(state, TICKETS_PAGE_FORM_NAME);
    const isCaptchaDisabled =
        getIsTicketSelectionButtonDisabledByCaptcha(state);
    const areSelectorsHidden = areQuantitySelectorsHidden(state);
    const isDisabled =
        isFormSubmittedBool || isCaptchaDisabled || areSelectorsHidden;

    return {
        validationErrorMessage,
        ctaText,
        // XXX: getFormButtonProps gives us isSubmitDisabled, but we have to override it because isPristine isn't
        // working for ticketSelection right now as it's not properly hooked up to redux form
        //
        // https://jira.evbhome.com/browse/EB-47911
        isDisabled,
    };
};

const mapStateToProps = (state) => {
    const selectedTickets = getSelectedTickets(state);

    return {
        areExpressCheckoutConditionsReady:
            areExpressCheckoutConditionsReady(state),
        areTicketsSelected: hasSelectedTickets(state),
        canMakePaymentsWithActiveCard: getCanMakePaymentsWithActiveCard(state),
        errors: state.app.errors,
        eventDate: getFormattedEventDate(state),
        eventTitle: getEventTitle(state),
        groupType: getEventGroupType(state),
        hasAdvancedTeamsEnabled: state.event.hasAdvancedTeamsEnabled,
        isAppRebrandActive: getIsAppRebrandActive(state),
        isApplePayElegible: isApplePayElegible(state),
        isDraftEvent: getIsDraftEvent(state),
        isExpressApplePayEnabled: isExpressApplePayEnabled(state),
        isExpressGooglePayEnabled: isExpressGooglePayEnabled(state),
        isExpressPayPalEnabled: isExpressPaypalEnabled(state),
        isFormSubmitting: isSubmitting(TICKETS_PAGE_FORM_NAME)(state),
        isFreeEvent: getIsFreeEvent(state),
        isMakingApplePayPayment: getIsMakingApplePayPayment(state),
        isMakingGooglePayPayment: getIsMakingGooglePayPayment(state),
        isMakingPaypalPayment: getIsMakingPaypalPayment(state),
        isModal: state.app.isModal,
        isMultiTicket: getIsMultiTicketEvent(state),
        isThereGooglePayPaymentRequest: !!getGooglePayPaymentRequest(state),
        isProd: state.app.isProd,
        isRegEvent: state.app.isRegEvent,
        isReservedSeating: getIsReservedSeating(state),
        isTicketSelectionCompactSizeFlow:
            getIsTicketSelectionCompactSizeFlow(state),
        selectedTickets,
        shouldShowBackToSeriesLink: shouldShowBackToSeriesLink(state),
        shouldShowTicketCategoryGroupHeaders:
            getShouldShowTicketCategoryGroupHeaders(state),
        shouldShowWaitingRoomCTA: state.waitingRoom.isWaitingRoomShown,
        showNewlyCreatedGroupNotification: getIsNewlyCreatedGroup(state),
        submitPromoCodeFormFailed:
            state.form[PROMO_CODE_FORM_NAME]?.submitFailed ?? false,
        templateProps: getTemplateProps({
            state,
            formName: TICKETS_PAGE_FORM_NAME,
        }),
        ticketClassesByCategory: getFormattedTicketClassesByCategory(state),
        ticketReseller: _getEventResellerProps(state.event),
        totalCostDisplay: getTotalCostDisplay(state),
        urgencySignals: getUrgencySignals(state),
        waitlistSettings: state.event.waitlistSettings,
        widgetParentUrl: appSelectors.getWidgetParentUrl(state.app),
        isSeriesInfiniteCalendar: isSeriesInfiniteCalendar(state),
        ...getTicketClassMetaProps,
        ...getFooterProps(state),
        ...getPromoCodeFormProps(state),
    };
};

export const mapDispatchToProps = (dispatch) => ({
    onPageWillMount: async (shouldShowPromoCodeForm) => {
        dispatch(setIsMakingPaypalPayment(false));
        dispatch(setIsMakingApplePayPayment(false));
        dispatch(setIsMakingGooglePayPayment(false));

        dispatch(initializeGryllsAction());
        dispatch(updateWaitingRoomStepAction(CHECKOUT_STEP_CART));
        // Temporarily recording an action to know when the promo code
        // is shown in the checkoutWidget https://jira.evbhome.com/browse/EB-126752
        if (shouldShowPromoCodeForm) {
            // Events with promo codes are disqualified from Checkout in Listings
            // experiment so when rendering TicketSelectionCompactSizePage we
            // never have shouldShowPromoCodeForm=true and it's ok to hardcode
            // TICKETS_FULL_SIZE_PATH
            dispatch(trackPromoCodeFormShown(TICKETS_FULL_SIZE_PATH));
        }

        try {
            dispatch(setIsApplePaySupported());
            await dispatch(getExpressCheckoutConditions());
            await dispatch(getNosRollout());
        } catch (e) {
            dispatch(setHasAsyncErrors(true));
            logger.error(e);
        }
        dispatch(initializeApplePayIfExpressCheckout());
        dispatch(initializeGooglePayIfExpressCheckout());

        logEvent(STATSIG_EVENT_NAMES.TICKET_STEP_PAGE_VIEW);
    },
    onSubmit: (data = {}) => {
        dispatch(loadEmailPreferences());

        if (data.isApplePay) {
            return dispatch(startExpressCheckoutApplePaySession());
        }

        if (
            data.payment?.applePayBraintreeNonce ||
            data.payment?.paypalBraintreeNonce ||
            data.payment?.googlePayBraintreeNonce
        ) {
            return dispatch(createAndPlaceOrderExpressCheckout(data.payment));
        }
        dispatch(notifyParentTicketSelectionSubmit());
        return dispatch(submitFromTicketSelectionPage());
    },
    onSubmitFail: () => {
        dispatch(setIsMakingPaypalPayment(false));
        dispatch(setIsMakingApplePayPayment(false));
        dispatch(setIsMakingGooglePayPayment(false));
    },
    onQuantityChange: (data) => {
        // We intentionally assign all these events to TICKETS_FULL_SIZE_PATH,
        // even though sometimes they come from TicketSelectionCompactSizePage
        // We don't care because Google Analytics tracking is to be deprecated
        dispatch(ticketQuantityChange(data, TICKETS_FULL_SIZE_PATH));
        dispatch(updateSelectedPaymentConstraints());
        dispatch(notifyParentTicketQuantityChange(data));
    },
    onVariantQuantityChange: (data) => dispatch(ticketVariantChange(data)),
    onDonationChange: (data) => dispatch(ticketDonationChange(data)),
    promoCodeFormHandlers: {
        ...getPromoCodeFormActionHandlers(dispatch),
        // Note: the onSubmit function should dispatch the action, but
        // should NOT return anything. Keep the curly braces!
        onSubmit: (data) => {
            // Events with promo codes are disqualified from Checkout in Listings
            // experiment so TicketSelectionCompactSizePage never uses
            // promoCodeFormHandlers and it's ok to hardcode TICKETS_FULL_SIZE_PATH
            dispatch(promoCodeSubmitAction(data, TICKETS_FULL_SIZE_PATH))
                // Do nothing, errors are caught in promoCodeSubmitAction
                .catch(() => null);
        },
    },
    onJoinWaitlist: () => {
        // onJoinWaitlist is never called in TicketSelectionCompactSizePage so
        // it's ok to hardcode TICKETS_FULL_SIZE_PATH here
        dispatch(trackJoinWaitlistClick(TICKETS_FULL_SIZE_PATH, TICKET_LEVEL));
        dispatch(push(joinPath(BASE_URL, WAITLIST_PATH)));
    },
    onBackToSeries: () => {
        // onBackToSeries is never called in TicketSelectionCompactSizePage so
        // it's ok to hardcode TICKETS_FULL_SIZE_PATH here
        dispatch(trackBackClick(TICKETS_FULL_SIZE_PATH));
        dispatch(navigateToSeriesPageAction());
    },
    onBackToGroupTypeSelection: () => {
        // onBackToGroupTypeSelection is never called in TicketSelectionCompactSizePage
        // so it's ok to hardcode TICKETS_FULL_SIZE_PATH here
        dispatch(trackBackClick(TICKETS_FULL_SIZE_PATH));
        dispatch(resetEventAndTickets());
        dispatch(resetGroupRegistrationAction());
        dispatch(goToPageBeforeJoinAction());
    },
    onCartIconClick: (isPaneOpen) => {
        logEvent(STATSIG_EVENT_NAMES.CHECKOUT_ALL_CART_SUMMARY_CLICK);
        // onCartIconClick is never called in TicketSelectionCompactSizePage so
        // it's ok to hardcode TICKETS_FULL_SIZE_PATH here
        dispatch(trackCartIconClick(isPaneOpen, TICKETS_FULL_SIZE_PATH));
    },
    onTicketSelectionLayoutStabilized: (contentHeight) => {
        return dispatch(
            notifyParentTicketSelectionLayoutStabilized(contentHeight),
        );
    },
    onPageFullyLoaded: () => {
        dispatch(notifyParentInitialPageFullyLoaded());
    },
    onSetTicketsMessageReceived: (data) => {
        dispatch(updateSelectedTickets(data));
        dispatch(updateSelectedPaymentConstraints());
    },
    resetPaypalScriptStatus: () =>
        dispatch(setPaypalScriptStatusAction(PaypalScriptStatus.init)),
    refreshGooglePayPaymentRequest: () =>
        dispatch(updateGooglePayPaymentRequest()),
});

export const connectTicketSelectionPage = (ComponentClass) =>
    connect(mapStateToProps, mapDispatchToProps)(ComponentClass);
